import { forwardRef } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

// `neutro` (RH-MENU, 03/10/2026): porta NOVA e opcional — o `info` desta casa é o vermelho IGM, e um aviso que só
// informa (ex.: "ainda não está no Omie") não pode ter cara de erro. Nada muda para quem já usava as outras quatro.
type AlertVariant = 'info' | 'success' | 'warning' | 'danger' | 'neutro';

const VARIANT_CLASSES: Record<AlertVariant, { bg: string; border: string; text: string; rail: string; icon: string }> = {
  info: {
    bg: 'bg-red-soft/50 dark:bg-red-soft-dark/40',
    border: 'border-red-soft dark:border-red-soft-dark',
    text: 'text-red-deep dark:text-red-glow',
    rail: 'bg-red',
    icon: 'bg-red',
  },
  success: {
    bg: 'bg-[rgba(79,126,63,0.08)]',
    border: 'border-[rgba(79,126,63,0.2)]',
    text: 'text-success',
    rail: 'bg-success',
    icon: 'bg-success',
  },
  warning: {
    bg: 'bg-[rgba(176,127,45,0.08)]',
    border: 'border-[rgba(176,127,45,0.2)]',
    text: 'text-warning',
    rail: 'bg-warning',
    icon: 'bg-warning',
  },
  danger: {
    bg: 'bg-red-soft dark:bg-red-soft-dark',
    border: 'border-red-soft dark:border-red-soft-dark',
    text: 'text-red-deep dark:text-red-glow',
    rail: 'bg-red',
    icon: 'bg-red',
  },
  neutro: {
    bg: 'bg-paper-warm dark:bg-paper-dark-warm',
    border: 'border-line dark:border-line-dark',
    text: 'text-ink-2 dark:text-ink-dark-2',
    rail: 'bg-ink-3',
    icon: 'bg-ink-3 dark:bg-ink-dark-3',
  },
};

interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
  title?: string;
  /** Mostra rail vertical lateral em vez de borda completa */
  rail?: boolean;
  /** Ícone customizado. Default: ! dentro de círculo */
  icon?: ReactNode | false;
}

/**
 * Alert — mensagem destacada de aviso/info/erro.
 *
 * Variants: info (vermelho IGM), success (verde), warning (âmbar), danger (vermelho forte), neutro (papel e tinta —
 * aviso que só informa).
 *
 * <Alert variant="info" title="3 orçamentos sem resposta">
 *   Recomendamos follow-up imediato.
 * </Alert>
 *
 * <Alert variant="warning" rail>
 *   Cliente sem retorno há 12 dias.
 * </Alert>
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ variant = 'info', title, rail = false, icon, className, children, ...props }, ref) => {
    const v = VARIANT_CLASSES[variant];
    const showIcon = icon !== false;

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          'rounded-xl p-4',
          rail
            ? `relative pl-5 ${v.bg} before:absolute before:left-0 before:top-3 before:bottom-3 before:w-[3px] before:rounded-r before:${v.rail}`
            : `border ${v.border} ${v.bg}`,
          className
        )}
        {...props}
      >
        <div className="flex gap-3">
          {showIcon && (
            <span
              className={cn(
                'w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5',
                v.icon
              )}
              aria-hidden="true"
            >
              {icon ?? '!'}
            </span>
          )}
          <div className="flex-1 min-w-0">
            {title && (
              <div className={cn('font-medium text-sm', v.text)}>{title}</div>
            )}
            {children && (
              <div className={cn('text-sm opacity-90', v.text, title && 'mt-1')}>{children}</div>
            )}
          </div>
        </div>
      </div>
    );
  }
);
Alert.displayName = 'Alert';
